import EmailOutlinedIcon from "@mui/icons-material/EmailOutlined";
import PhoneOutlinedIcon from "@mui/icons-material/PhoneOutlined";
import { Box, Button, Chip, Divider } from "@mui/material";
import { useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import CustomTextArea from "../../../../../components/CustomTextArea";
import Flex from "../../../../../components/Flex";
import SideSheetDrawer from "../../../../../components/SidesheetDrawer";
import Text from "../../../../../components/Text";
import DeleteModal from "../../../../../components/app/DeleteModal";
import SuccessModal, {
  useSuccess,
} from "../../../../../components/SuccessModal";
import useDisclosure, {
  DisclosureHost,
} from "../../../../../hooks/use-disclosure";
import { useSnackbar } from "../../../../../hooks/useSnackbar";
import { PATIENT_INQUIRY_STATUS_COUNTS_KEY } from "../../../../../queries/collaboration/patient-inquiry/use-get-patient-inquiry-status-counts";
import { useUpdatePatientInquiryStatus } from "../../../../../queries/collaboration/patient-inquiry/use-update-patient-inquiry-status";
import { PatientInquiryStatusUpdateStatusEnum } from "../../../../../services";
import { formatDateTime } from "../../../../../utils/date-time-utility";
import {
  formatPhoneNumber,
  getAddressFormat,
  getSuccessSnackbar,
  replaceUnderScoreAddSpace,
  showToastOnApiCall,
} from "../../../../../utils/utils";
import SendBulkEmailModal from "../../communications/email/SendBulkEmailModal";
import { ContactDialModal } from "../../dashboard/twilio/ContactDial";
import DenyAppointmentModal from "./DenyAppointmentModal";
import {
  INQUIRY_PATIENT_TYPE_EXISTING,
  INQUIRY_SOURCE_WEBSITE,
  getStatusUi,
} from "./patient-inquiry-constants";

interface PatientInquiryDetailsDialogProps {
  disclosureHost: DisclosureHost;
  inquiryId: string;
  xTENANTID?: string;
  inquiryDetails?: Record<string, any>;
}

const getAnyValue = (obj: Record<string, any> | undefined, keys: string[]) => {
  if (!obj) return undefined;
  for (const key of keys) {
    const value = obj?.[key];
    if (value !== undefined && value !== null && value !== "") return value;
  }
  return undefined;
};

const renderDateTime = (value: unknown) => {
  if (!value || typeof value !== "string") return "-";
  return formatDateTime(value) || "-";
};

const PatientInquiryDetailsDialog = ({
  disclosureHost,
  inquiryId,
  xTENANTID,
  inquiryDetails,
}: PatientInquiryDetailsDialogProps) => {
  const showSnackbar = useSnackbar();
  const queryClient = useQueryClient();
  const { mutateAsync: updateInquiryStatus } = useUpdatePatientInquiryStatus();
  const cancelDisclosure = useDisclosure(false);
  const denyDisclosure = useDisclosure(false);
  const [openedSuccess, { open: openSuccess, close: closeSuccess }] =
    useSuccess(() => disclosureHost.close());

  const [cancelReason, setCancelReason] = useState("");
  const [successMessage, setSuccessMessage] = useState("");

  const patientId = getAnyValue(inquiryDetails, ["patientId"]);

  const patientPhone = String(
    getAnyValue(inquiryDetails, ["patientPhone", "phone", "contactNumber"]) ??
      ""
  );
  const patientEmail = String(
    getAnyValue(inquiryDetails, ["patientEmail", "email", "emailId"]) ?? ""
  );
  const patientName =
    getAnyValue(inquiryDetails, [
      "patientName",
      "name",
      "fullName",
      "patientFullName",
    ]) || "-";
  const status = String(getAnyValue(inquiryDetails, ["status"]) ?? "");
  const statusUi = getStatusUi(status);
  // const mode = String(
  //   getAnyValue(inquiryDetails, ["appointmentType", "mode", "type"]) ?? ""
  // ).toUpperCase();
  // const modeLabel = mode ? replaceUnderScoreAddSpace(mode.toLowerCase()) : "-";

  const appointmentId = String(getAnyValue(inquiryDetails, ["uuid"]) ?? "");
  const patientAddress = getAddressFormat(
    getAnyValue(inquiryDetails, ["address", "patientAddress", "fullAddress"])
  );

  const source = String(
    getAnyValue(inquiryDetails, ["source", "inquirySource"]) ?? ""
  ).toUpperCase();
  const patientType = String(
    getAnyValue(inquiryDetails, ["inquiryChange"]) ?? ""
  ).toUpperCase();

  // Only a website request from a patient who is not registered yet gets the
  // cancel-options modal, portal requests keep the plain reject flow. The API
  // sends these in mixed case, and only EXISTING marks a registered patient.
  const isWidgetNewPatientInquiry =
    source.includes(INQUIRY_SOURCE_WEBSITE) &&
    patientType !== INQUIRY_PATIENT_TYPE_EXISTING;

  const handleUpdateStatus = (nextStatus: "CONFIRMED") => {
    if (!inquiryId) return;
    if (!appointmentId) {
      showSnackbar({
        type: "error",
        message: "Appointment id is missing for this inquiry.",
      });
      return;
    }

    showToastOnApiCall({
      apiCall: updateInquiryStatus({
        inquiryId: appointmentId,
        patientInquiryStatusUpdate: {
          status: nextStatus as any,
        },
        xTENANTID,
      }),
      showSnackbar,
      onSave: (res: any) => {
        queryClient.invalidateQueries({
          queryKey: ["collaboration-patient-inquiry-list"],
        });
        queryClient.invalidateQueries({
          queryKey: ["patient-inquiry-by-id"],
        });
        // Confirming clears a pending inquiry, so the scheduling badge count
        // is refetched rather than left showing the pre-confirm number.
        queryClient.invalidateQueries({
          queryKey: [PATIENT_INQUIRY_STATUS_COUNTS_KEY],
        });

        setSuccessMessage(res?.data?.message ?? "");

        openSuccess();
      },
    });
  };

  const contactDialModalDisclosure = useDisclosure(false);
  const sendMailDisclosure = useDisclosure(false);

  const InfoRow = ({ label, value }: { label: string; value: string }) => (
    <Flex gap={1} flexWrap="wrap">
      <Text color="#616161" minWidth="140px">
        {label}
      </Text>
      <Text color="#616161">:</Text>
      <Text>{value || "-"}</Text>
    </Flex>
  );

  return (
    <>
      <SideSheetDrawer
        open={disclosureHost.opened}
        onClose={disclosureHost.close}
        title="Inquiry Details"
        w="714px"
        footerActions={
          status === "REJECTED" ? undefined : (
            <>
              <Button
                variant="text"
                startIcon={<PhoneOutlinedIcon />}
                disabled={!patientPhone}
                onClick={() => contactDialModalDisclosure.open(patientId)}
              >
                Call Patient
              </Button>
              <Button
                variant="text"
                startIcon={<EmailOutlinedIcon />}
                disabled={!patientEmail}
                onClick={() =>
                  sendMailDisclosure.open([
                    { uuid: patientId, name: patientName },
                  ])
                }
              >
                Email Patient
              </Button>
              {status !== "CONFIRMED" && (
                <Button
                  variant="outlined"
                  color="error"
                  onClick={() =>
                    isWidgetNewPatientInquiry
                      ? denyDisclosure.open()
                      : cancelDisclosure.open()
                  }
                >
                  Reject
                </Button>
              )}
              {status !== "CONFIRMED" && (
                <Button
                  variant="contained"
                  onClick={() => handleUpdateStatus("CONFIRMED")}
                >
                  Confirm
                </Button>
              )}
            </>
          )
        }
      >
        {contactDialModalDisclosure.opened && (
          <ContactDialModal
            disclosureHost={contactDialModalDisclosure}
            contactNumber={patientPhone}
          />
        )}

        {sendMailDisclosure.opened && (
          <SendBulkEmailModal
            disableAddPatient
            disClosureHost={sendMailDisclosure}
          />
        )}

        <Flex flexDirection="column" gap={1}>
          {/* <Box
            sx={{
              border: "1px solid #E0E0E0",
              borderRadius: 1,
              p: 1,
            }}
          >
            <Flex alignItems="flex-start" justifyContent="space-between" mb={1}>
              <Box>
                <Text fontSize="20px" fontWeight="medium">
                  {patientName}
                </Text>
                <Flex alignItems="center" gap={1} mt={0.5}>
                  <Text color="#7A7A7A">
                    {renderDateTime(
                      getAnyValue(inquiryDetails, ["patientDob", "dob", "birthDate"])
                    )}
                  </Text>
                </Flex>
                <Flex gap={3} mt={0.8} flexWrap="wrap">
                  <Flex alignItems="center" gap={0.7}>
                    <PhoneOutlinedIcon fontSize="small" color="disabled" />
                    <Text color="#616161">
                      {patientPhone ? formatPhoneNumber(patientPhone) : "-"}
                    </Text>
                  </Flex>
                  <Flex alignItems="center" gap={0.7}>
                    <EmailOutlinedIcon fontSize="small" color="disabled" />
                    <Text color="#616161">{patientEmail || "-"}</Text>
                  </Flex>
                </Flex>
              </Box>
              <Chip
                size="small"
                label={
                  <Flex alignItems="center" gap={0.5}>
                    {mode === "TELEHEALTH" ? (
                      <VideocamOutlinedIcon
                        fontSize="small"
                        sx={{ color: "#7C3AED" }}
                      />
                    ) : (
                      <PersonOutlinedIcon
                        fontSize="small"
                        sx={{ color: "#7C3AED" }}
                      />
                    )}
                    <Text c="#7C3AED">{modeLabel}</Text>
                  </Flex>
                }
                sx={{
                  bgcolor: "#F3E8FF",
                  borderColor: "#7C3AED",
                }}
                variant="outlined"
              />
            </Flex>

            <Flex mt={1}>
              <Chip
                size="small"
                label={statusUi.label}
                sx={{
                  color: statusUi.color,
                  bgcolor: statusUi.bgColor,
                  fontWeight: 500,
                }}
              />
            </Flex>
          </Box> */}

          <Box
            sx={{
              border: "1px solid #E0E0E0",
              borderRadius: 1,
              overflow: "hidden",
            }}
          >
            <Box bgcolor="#F5F6F8" px={1.5} py={1}>
              <Text fontWeight="medium">Patient Information</Text>
            </Box>
            <Flex flexDirection="column" gap={1} p={1.5}>
              <InfoRow
                label="Status"
                value={
                  (
                    <Chip
                      size="small"
                      label={statusUi.label}
                      sx={{
                        color: statusUi.color,
                        bgcolor: statusUi.bgColor,
                        fontWeight: 500,
                      }}
                    />
                  ) as any
                }
              />
              <InfoRow label="Patient Name" value={patientName} />
              <InfoRow
                label="Patient Type"
                value={
                  String(getAnyValue(inquiryDetails, ["inquiryChange"]) ?? "-")
                    ? replaceUnderScoreAddSpace(
                        String(
                          getAnyValue(inquiryDetails, ["inquiryChange"]) ?? "-"
                        )?.toLocaleLowerCase()
                      )
                    : "-"
                }
              />
              <InfoRow
                label="Contact Number"
                value={patientPhone ? formatPhoneNumber(patientPhone) : "-"}
              />
              <InfoRow label="Email ID" value={patientEmail || "-"} />
              <InfoRow label="Address" value={patientAddress} />
              <InfoRow
                label="Timezone"
                value={String(getAnyValue(inquiryDetails, ["timezone"]) ?? "-")}
              />
            </Flex>
          </Box>

          <Box
            sx={{
              border: "1px solid #E0E0E0",
              borderRadius: 1,
              overflow: "hidden",
            }}
          >
            <Box bgcolor="#F5F6F8" px={1.5} py={1}>
              <Text fontWeight="medium">Appointment Request Details</Text>
            </Box>
            <Flex flexDirection="column" gap={1} p={1.5}>
              <InfoRow
                label="Reason for Visit"
                value={String(
                  getAnyValue(inquiryDetails, [
                    "chiefComplaint",
                    "appointmentChiefComplaint",
                    "reasonForVisit",
                  ]) ?? "-"
                )}
              />
              <InfoRow
                label="Provider"
                value={String(
                  getAnyValue(inquiryDetails, [
                    "providerName",
                    "assignedProviderName",
                    "provider",
                  ]) ?? "-"
                )}
              />
              <InfoRow
                label="Appt.Date & Time"
                value={renderDateTime(
                  getAnyValue(inquiryDetails, [
                    "appDateAndTime",
                    "scheduledDateTime",
                    "appointmentTime",
                  ])
                )}
              />
              <InfoRow
                label="Type"
                value={
                  replaceUnderScoreAddSpace(
                    getAnyValue(inquiryDetails, ["inquiryChange"])
                  )?.toLowerCase() ?? "-"
                }
              />
              <InfoRow
                label="Payment Mode"
                value={String(
                  getAnyValue(inquiryDetails, [
                    "paymentMode",
                    "appointmentType",
                  ]) ?? "-"
                )}
              />
              <InfoRow
                label="Patient Note"
                value={String(
                  getAnyValue(inquiryDetails, ["patientNote"]) ?? "-"
                )}
              />
              <InfoRow label="Status" value={statusUi.label} />
              <InfoRow
                label="Inquiry Submitted"
                value={renderDateTime(getAnyValue(inquiryDetails, ["created"]))}
              />
              <InfoRow
                label="Source"
                value={String(
                  getAnyValue(inquiryDetails, ["source", "inquirySource"]) ??
                    "-"
                )}
              />
            </Flex>
          </Box>
        </Flex>

        <Divider sx={{ mt: 1.2 }} />
      </SideSheetDrawer>
      <DeleteModal
        disableOnSaveSnackbar
        w="450px"
        open={cancelDisclosure.opened}
        onClose={() => {
          cancelDisclosure.close();
          setCancelReason("");
        }}
        onConfirmation={async () => {
          if (!cancelReason?.trim()) {
            const err = {
              response: {
                data: { message: "Reason is a required field" },
              },
            };
            return Promise.reject(err);
          }
          if (!appointmentId) {
            const err = {
              response: {
                data: {
                  message: "Appointment id is missing for this inquiry.",
                },
              },
            };
            return Promise.reject(err);
          }

          return updateInquiryStatus({
            inquiryId: appointmentId,
            patientInquiryStatusUpdate: {
              status: PatientInquiryStatusUpdateStatusEnum.Rejected,
              rejectReason: cancelReason.trim(),
            },
            xTENANTID,
          }).then(async (res) => {
            showSnackbar(getSuccessSnackbar(res));
            await Promise.all([
              queryClient.invalidateQueries({
                queryKey: ["collaboration-patient-inquiry-list"],
              }),
              queryClient.invalidateQueries({
                queryKey: ["patient-inquiry-by-id"],
              }),
              queryClient.invalidateQueries({
                queryKey: [PATIENT_INQUIRY_STATUS_COUNTS_KEY],
              }),
            ]);
            setCancelReason("");
            cancelDisclosure.close();
            disclosureHost.close();
            return res;
          });
        }}
        content={
          (
            <Box>
              <Text align="center">
                You want to reject this patient appointment request.
              </Text>
              <Text align="center">
                Please specify the reason for the rejection.
              </Text>
            </Box>
          ) as any
        }
        input={
          <Box width="100%">
            <CustomTextArea
              required
              label="Reason"
              value={cancelReason}
              minRows={5}
              placeholder="Enter reason here..."
              onChange={(e) => {
                setCancelReason(e.currentTarget.value);
              }}
            />
          </Box>
        }
        confirmButtonText="Reject"
        cancelButtonText="Cancel"
      />
      {denyDisclosure.opened && (
        <DenyAppointmentModal
          disclosureHost={denyDisclosure}
          appointmentId={appointmentId}
          patientName={patientName}
          providerName={inquiryDetails?.providerName}
          appointmentDateTime={inquiryDetails?.appDateAndTime}
          onDenied={disclosureHost.close}
        />
      )}
      <SuccessModal
        label={successMessage}
        opened={openedSuccess}
        onClose={closeSuccess}
      />
    </>
  );
};

export default PatientInquiryDetailsDialog;
